import React, { useState } from 'react';
import {
  Box,
  Button,
  Stack,
  Typography,
  TextField,
  Link,
  Paper,
  Avatar,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, deleteUser, sendPasswordResetEmail } from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, setDoc, getDoc, collection, query, getDocs, where } from 'firebase/firestore';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import EmailIcon from '@mui/icons-material/Email';

// 사용자 색상 팔레트
const USER_COLORS = [
  '#E57373', '#F06292', '#BA68C8', '#9575CD', '#7986CB', 
  '#64B5F6', '#4FC3F7', '#4DD0E1', '#4DB6AC', '#81C784',
  '#AED581', '#DCE775', '#FFF176', '#FFD54F', '#FFB74D',
  '#FF8A65', '#A1887F', '#90A4AE'
];

export const Auth: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const { enqueueSnackbar } = useSnackbar();

  // 사용되지 않은 색상 가져오기
  const getAvailableColor = async () => {
    const usersSnapshot = await getDocs(collection(db, 'users'));
    const usedColors = new Set(
      usersSnapshot.docs.map(doc => doc.data().color)
    );
    
    const availableColors = USER_COLORS.filter(color => !usedColors.has(color));
    if (availableColors.length === 0) {
      // 모든 색상이 사용 중인 경우 랜덤하게 선택
      return USER_COLORS[Math.floor(Math.random() * USER_COLORS.length)];
    }
    
    return availableColors[Math.floor(Math.random() * availableColors.length)];
  };

  // 초대 코드 검증
  const validateInviteCode = async (code: string) => {
    try {
      // 공통 초대 코드
      const commonInviteCode = "TALKIE2024"; // 공통 초대 코드
      
      if (code.trim().toUpperCase() !== commonInviteCode) {
        throw new Error('유효하지 않은 초대 코드입니다.');
      }

      return true;
    } catch (error) {
      console.error('Error validating invite code:', error);
      throw error;
    }
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      if (isSignUp) {
        // 초대 코드 검증
        if (!inviteCode.trim()) {
          throw new Error('초대 코드를 입력해주세요.');
        }
        
        try {
          await validateInviteCode(inviteCode.trim());
        } catch (error: any) {
          throw new Error(error.message || '유효하지 않은 초대 코드입니다.');
        }

        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const userColor = await getAvailableColor();
        
        // 사용자 정보 저장
        await setDoc(doc(db, 'users', userCredential.user.uid), {
          email: userCredential.user.email,
          color: userColor,
          createdAt: new Date(),
          approved: true,
          isAdmin: false
        });

        // 입장 메시지 생성
        await setDoc(doc(collection(db, 'messages')), {
          type: 'system',
          text: `${userCredential.user.email}님이 입장하셨습니다.`,
          createdAt: new Date(),
          uid: 'system'
        });

        enqueueSnackbar('회원가입이 완료되었습니다!', { variant: 'success' });
      } else {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        
        // 승인된 사용자인지 확인
        const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
        if (!userDoc.exists() || !userDoc.data().approved) {
          await signOut(auth);
          throw new Error('승인되지 않은 사용자입니다.');
        }

        // 입장 메시지 생성
        await setDoc(doc(collection(db, 'messages')), {
          type: 'system',
          text: `${userCredential.user.email}님이 입장하셨습니다.`,
          createdAt: new Date(),
          uid: 'system'
        });

        enqueueSnackbar('환영합니다!', { variant: 'success' });
      }
      setEmail('');
      setPassword('');
      setInviteCode('');
    } catch (error: any) {
      console.error('Authentication error:', error);
      
      // 회원가입 실패 시 생성된 계정 삭제
      if (isSignUp && auth.currentUser) {
        try {
          await deleteUser(auth.currentUser);
        } catch (deleteError) {
          console.error('Error deleting user:', deleteError);
        }
      }
      
      enqueueSnackbar(error.message || getErrorMessage(error.code), { 
        variant: 'error',
        autoHideDuration: 5000
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    setIsLoading(true);
    try {
      // 퇴장 메시지 생성
      if (auth.currentUser) {
        await setDoc(doc(collection(db, 'messages')), {
          type: 'system',
          text: `${auth.currentUser.email}님이 퇴장하셨습니다.`,
          createdAt: new Date(),
          uid: 'system'
        });
      }
      
      await signOut(auth);
      enqueueSnackbar('로그아웃 되었습니다.', { variant: 'info' });
    } catch (error) {
      console.error('Error signing out:', error);
      enqueueSnackbar('로그아웃 실패', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const getErrorMessage = (errorCode: string) => {
    switch (errorCode) {
      case 'auth/email-already-in-use':
        return '이미 사용 중인 이메일입니다.';
      case 'auth/invalid-email':
        return '유효하지 않은 이메일 형식입니다.';
      case 'auth/operation-not-allowed':
        return '이메일/비밀번호 로그인이 비활성화되어 있습니다.';
      case 'auth/weak-password':
        return '비밀번호는 최소 6자 이상이어야 합니다.';
      case 'auth/user-not-found':
        return '등록되지 않은 이메일입니다.';
      case 'auth/wrong-password':
        return '잘못된 비밀번호입니다.';
      default:
        return '인증 과정에서 오류가 발생했습니다.';
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleResetPassword = async () => {
    if (!resetEmail.trim()) {
      enqueueSnackbar('이메일을 입력해주세요.', { variant: 'error' });
      return;
    }

    setIsLoading(true);
    try {
      await sendPasswordResetEmail(auth, resetEmail);
      enqueueSnackbar('비밀번호 재설정 이메일이 발송되었습니다.', { variant: 'success' });
      setResetPasswordOpen(false);
      setResetEmail('');
    } catch (error: any) {
      console.error('Error sending password reset email:', error);
      enqueueSnackbar(
        error.code === 'auth/user-not-found'
          ? '등록되지 않은 이메일입니다.'
          : '비밀번호 재설정 이메일 발송에 실패했습니다.',
        { variant: 'error' }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const ResetPasswordDialog = () => (
    <Dialog 
      open={resetPasswordOpen} 
      onClose={() => !isLoading && setResetPasswordOpen(false)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>비밀번호 재설정</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          가입한 이메일 주소를 입력하시면 비밀번호 재설정 링크를 보내드립니다.
        </Typography>
        <TextField
          autoFocus
          margin="dense"
          label="이메일"
          type="email"
          fullWidth
          variant="outlined"
          value={resetEmail}
          onChange={(e) => setResetEmail(e.target.value)}
          disabled={isLoading}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={() => setResetPasswordOpen(false)} 
          disabled={isLoading}
        >
          취소
        </Button>
        <Button 
          onClick={handleResetPassword}
          variant="contained"
          disabled={isLoading}
        >
          {isLoading ? <CircularProgress size={24} /> : '재설정 링크 발송'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Paper
      elevation={2}
      sx={{
        p: { xs: 2, sm: 3 },
        width: '100%',
        maxWidth: '400px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      {auth.currentUser ? (
        <Stack
          spacing={2}
          alignItems="center"
          sx={{ width: '100%' }}
        >
          <Avatar
            sx={{
              bgcolor: 'primary.main',
              width: 56,
              height: 56,
            }}
          >
            {auth.currentUser.email?.[0].toUpperCase() || 'U'}
          </Avatar>
          <Typography variant="h6" sx={{ mt: 1 }}>
            환영합니다!
          </Typography>
          <Typography color="text.secondary" align="center" sx={{ wordBreak: 'break-all' }}>
            {auth.currentUser.email}
          </Typography>
          <Button
            onClick={handleSignOut}
            variant="outlined"
            color="secondary"
            startIcon={isLoading ? <CircularProgress size={20} /> : <LogoutIcon />}
            disabled={isLoading}
            sx={{ mt: 2 }}
          >
            {isLoading ? '로그아웃 중...' : '로그아웃'}
          </Button>
        </Stack>
      ) : (
        <>
          <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
            <LockOutlinedIcon />
          </Avatar>
          <Typography component="h1" variant="h5" sx={{ mb: 3 }}>
            {isSignUp ? '회원가입' : '로그인'}
          </Typography>
          {isSignUp && (
            <Alert severity="info" sx={{ mb: 2, width: '100%' }}>
              채팅방 입장을 위해서는 초대 코드가 필요합니다.
            </Alert>
          )}
          <form 
            onSubmit={handleAuth} 
            style={{ width: '100%' }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAuth(e);
              }
            }}
          >
            <Stack spacing={2}>
              <TextField
                required
                label="이메일"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="이메일을 입력하세요"
                fullWidth
                variant="outlined"
                autoComplete="email"
                disabled={isLoading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                required
                label="비밀번호"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="비밀번호를 입력하세요"
                fullWidth
                variant="outlined"
                autoComplete={isSignUp ? 'new-password' : 'current-password'}
                disabled={isLoading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={togglePasswordVisibility}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {isSignUp && (
                <TextField
                  required
                  label="초대 코드"
                  type="text"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value)}
                  placeholder="초대 코드를 입력하세요"
                  fullWidth
                  variant="outlined"
                  disabled={isLoading}
                />
              )}
              <Stack 
                direction="row" 
                justifyContent="space-between" 
                alignItems="center"
                width="100%"
              >
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => {
                    if (!isLoading) {
                      setIsSignUp(!isSignUp);
                      setEmail('');
                      setPassword('');
                      setInviteCode('');
                    }
                  }}
                  disabled={isLoading}
                  sx={{
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {isSignUp ? '이미 계정이 있으신가요? 로그인' : '계정이 없으신가요? 회원가입'}
                </Link>
                {!isSignUp && (
                  <Link
                    component="button"
                    variant="body2"
                    onClick={(e) => {
                      e.preventDefault();
                      if (!isLoading) {
                        setResetPasswordOpen(true);
                      }
                    }}
                    disabled={isLoading}
                    sx={{
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    비밀번호를 잊으셨나요?
                  </Link>
                )}
              </Stack>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                disabled={isLoading}
                sx={{ mt: 2 }}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  isSignUp ? '회원가입' : '로그인'
                )}
              </Button>
            </Stack>
          </form>
          <ResetPasswordDialog />
        </>
      )}
    </Paper>
  );
}; 